/**
 * Vocabulary shared across the sales-channel switch flow.
 *
 * This lives here rather than in salesChannels/helpers.tsx on purpose: helpers.tsx is imported from six places
 * outside this folder (orders, bulkVariation, selectSourceStore, wix, shopifyStoreSettings, platformIntegrationSettings),
 * all of them purely for generateStoreNumbers / salesChannelList / checkUrl. Putting switch vocabulary there would make
 * every one of them transitively depend on the switch domain.
 *
 * Keep this module free of graphql tags and generated-type imports so it stays cheap to import.
 */

/** Numeric task states as returned by productSwitcherTask(s).state. */
export enum TASKSTATUS {
    Waiting = 0,
    Processing = 1,
    Completed = 2,
    Error = 3
}

/** The plan-preparation task can be inspected and surfaced again after it fails. */
export const CREATE_CUSTOM_PLAN_FOR_SELLER_TASK_TYPE = 'CREATE_CUSTOM_PLAN_FOR_SELLER';

export interface SellerPlanCheckInfo {
    isLegacyPlan: boolean;
    isV1Plan: boolean;
    isProductLimitReached: boolean;
    maxProductsGrow: number | null;
    maxProductsScale: number | null;
    currentProductCount: number;
}

/** Stages of the switch that are polled via startPollingTask (persisted so the polling dialog can resume after a page refresh). */
export type SwitchPollingFlowType = 'shopify' | 'nonShopify' | 'deleteRecurringCharges' | 'createCustomPlanShopify' | 'createCustomPlanNonShopify';

export const UNSUBSCRIBE_TASK_STORAGE_KEY = 'unsubscribingPlanTask';
export const SWITCH_SUBSCRIPTION_RETURN_STORAGE_KEY = 'switchSubscriptionReturn';
/** Persists the payment-method setup task while the seller is redirected outside the switch flow. */
export const PAYMENT_METHOD_SETUP_REQUIRED_STORAGE_KEY = 'salesChannelSwitchPaymentMethodSetupRequired';

/**
 * The switch always moves products from a secondary sales channel onto the master integration. Both IDs are
 * interchangeable strings, so they are always passed by name: a positional swap migrates the wrong direction and cannot be undone.
 */
export interface SwitchSalesChannelIDs {
    masterSalesChannelID: string;
    secondarySalesChannelID: string;
}

/**
 * Persisted to localStorage, so these property names are a wire format, not an implementation detail: a build that renames
 * them strands any seller who is mid-flow when it ships. Always write them explicitly — never via object shorthand.
 */
export interface UnsubscribeTaskStoragePayload {
    taskID: string;
    /** The secondary sales channel — the one being promoted to master. */
    salesChannelID: string;
    masterSalesChannelID: string;
    flowType?: SwitchPollingFlowType;
    dialogId?: string;
}

export interface SwitchSubscriptionReturnStoragePayload {
    masterSalesChannelID: string;
    secondarySalesChannelID: string;
}

export interface PaymentMethodSetupStoragePayload {
    taskID: number;
}

/**
 * Dialogs are keyed by string identity: the dialog reducer replaces in place on a matching id and reuses the
 * instance when the element type also matches. These values are load-bearing — keep them byte-identical.
 */
export const SWITCH_DIALOG_IDS = {
    unsubscribingPlan: 'unsubscribing-plan',
    creatingCustomPlan: 'creating-custom-plan',
    customPlanTaskError: 'custom-plan-task-error',
    customPlanFinancialPreview: 'custom-plan-financial-preview',
    creatingSwitchTask: 'creating-switch-task',
    switchTaskCreated: 'switch-task-created',
    paymentMethodSetup: 'payment-method-setup',
    paymentSuccess: 'payment-success-dialog',
    switchSalesChannel: 'switch-sales-channel-dialog',
    unsubscribeTaskError: 'unsubscribe-task-error',
    unsubscribeTaskTimeout: 'unsubscribe-task-timeout',
    redirectingToShopify: 'redirecting-to-shopify',
    switchNotSupported: 'switch-not-supported'
} as const;

/**
 * A task the switch flow is still waiting on. Note this is deliberately NOT the predicate used to decide whether the
 * "Set as Master Integration" action is offered — that one also excludes Error.
 */
export const isTaskPending = (state: number): boolean => state === TASKSTATUS.Waiting || state === TASKSTATUS.Processing;
